import isNumber from "lodash/isNumber";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { Select } from "PFComponents/select";
import { useRoleName } from "PFCore/helpers/activities";
import { getProfileName, isProfileActive } from "PFCore/helpers/profile";
import { useIsShortlistingPermitted } from "PFCore/helpers/shortlists";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { fetchActivities } from "PFCore/services/activities";
import { FetchOptionsResponse, Option } from "PFTypes";
import { Activity, Profile } from "PFTypes";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";

import { ShortlistButton } from "./parts/shortlist_button";
import { UndoButton } from "./parts/undo_button";
import css from "./shortlist_for_profile_button.module.scss";

type ShortlistForProfileButtonProps = {
  className?: string;
  profile: Profile;
  small?: boolean;
};

export const ShortlistForProfileButton = ({ className, profile, small }: ShortlistForProfileButtonProps) => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("profiles");
  const isShortlistingPermitted = useIsShortlistingPermitted();
  const getRoleName = useRoleName();
  const roleTemplate = useTemplate(TemplateKey.Role);
  const ownGroup = currentProfile.permissionsGroup;
  const isResourcer = ownGroup.permissions.find(({ name, permitted }) => name === "resourcer" && permitted);
  const canShortlist =
    !!roleTemplate?.create_permitted &&
    (isResourcer || isShortlistingPermitted()) &&
    isProfileActive(profile);

  const [shown, setShown] = useState(false);
  const [isShortlistInfoVisible, setIsShortlistInfoVisible] = useState(false);
  const [isJustAdded, setIsJustAdded] = useState(false);
  const [selected, setSelected] = useState<Activity | null>(null);

  const {
    data: shortlists,
    isFetching: isLoadingShortlists,
    dataUpdatedAt: shortlistsDataUpdatedAt
  } = useActivityShortlists(
    selected?.id || -1,
    {},
    {
      enabled: isNumber(selected?.id)
    }
  );

  useEffect(() => {
    if (!shortlists) {
      return;
    }
    setIsShortlistInfoVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistsDataUpdatedAt]);

  const shortlist = useMemo(
    () => shortlists?.entries?.find?.((entry) => entry.profile.id === profile.id) ?? null,
    [profile.id, shortlists?.entries]
  );

  const handleNewShortlist = () => {
    setIsJustAdded(true);
  };

  const handleFetchOptions = ({ searchTerm, ids }): Promise<FetchOptionsResponse<Activity>> => {
    setIsShortlistInfoVisible(false);
    setIsJustAdded(false);

    if (ids) {
      return Promise.resolve({
        entries: selected?.id ? [{ id: selected.id, value: selected.name, original: selected }] : [],
        meta: { total: 1 }
      });
    }

    return fetchActivities({
      filters: {
        fields: {
          term: searchTerm,
          state: ["draft", "new"],
          templateKey: [TemplateKey.Role, TemplateKey.AuditRole],
          ...(isShortlistingPermitted() ? {} : { ownerId: currentProfile.id })
        }
      },
      order: {
        name: ["activity.name"],
        direction: "asc",
        text: "Alphabetically (A-Z)" //should not be translated
      }
    }).then((response) => ({
      ...response,
      entries: response.entries.map(({ activity }) => ({
        id: activity.id,
        value: activity.name ?? String(activity.id),
        displayElement: getRoleName(activity),
        original: activity
      }))
    }));
  };

  if (!canShortlist) {
    return null;
  }

  return (
    <>
      <Button
        className={className}
        aria-label={t("shortlistForProfile.shortlistName", { name: profile.firstName })}
        text={t("shortlistForProfile.shortlist")}
        small={small}
        onClick={(event) => {
          event.stopPropagation();
          setShown(true);
        }}
      />
      {shown && (
        <Modal
          title={t("shortlistForProfile.shortlistName", { name: getProfileName(profile) })}
          onClose={() => setShown(false)}
          showOKButton={false}
        >
          <Select<Activity>
            placeholder={selected ? "" : t("shortlistForProfile.startTyping")}
            value={selected?.id}
            renderDisplayValue={() => selected?.name ?? ""}
            onChange={(option: Option<Activity>) => setSelected(option.original ?? null)}
            fetchOptions={handleFetchOptions}
          />

          <div className={css.action}>
            {isLoadingShortlists && !isShortlistInfoVisible && <LoadingDots />}

            {isShortlistInfoVisible &&
              selected &&
              (shortlist ? (
                <>
                  <div className={css.tip}>
                    <Trans
                      i18nKey={
                        isJustAdded
                          ? "shortlistForProfile.nameHasBeenShortlisted"
                          : "shortlistForProfile.nameAlreadyShortlisted"
                      }
                      t={t}
                      values={{ name: getProfileName(profile), selected: selected.name }}
                      components={[
                        <Link
                          to={`/activities/${selected.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          key="0"
                        />
                      ]}
                    />
                  </div>
                  <UndoButton
                    activityId={selected.id}
                    shortlist={shortlist}
                    isLoadingShortlists={isLoadingShortlists}
                  />
                </>
              ) : (
                <>
                  <div className={css.tip}>
                    <Trans
                      i18nKey="shortlistForProfile.shortlistNameTo"
                      t={t}
                      values={{ name: getProfileName(profile), selected: selected.name }}
                      components={[
                        <Link
                          to={`/activities/${selected.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          key="0"
                        />
                      ]}
                    />
                    :
                  </div>
                  <ShortlistButton
                    profile={profile}
                    activity={selected}
                    isLoadingShortlists={isLoadingShortlists}
                    onShortlistCreated={handleNewShortlist}
                  />
                </>
              ))}
          </div>
        </Modal>
      )}
    </>
  );
};
